import {
    animate,
    state,
    style,
    transition,
    trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';
import { BehaviorSubject, timer } from 'rxjs';
import { LongPressDirective } from 'src/app/core/directives/long-press.directive';

type LongPressState = 'active' | 'submitted';

@Component({
    selector: 'long-press-btn',
    standalone: true,
    imports: [CommonModule, LongPressDirective],
    template: `
        <button
            type="button"
            class="progress-button"
            [longPress]="duration"
            [longPressThreshold]="threshold"
            (longPressStart)="onPressStart($event)"
            (longPressCancel)="onPressCancel($event)"
            (longPressFinish)="onFinish($event)"
            (normalClick)="onNormalClick($event)"
            [disabled]="disabled"
        >
            <ng-content></ng-content>

            <svg
                class="progress-ring"
                viewBox="0 0 100 100"
                *ngIf="showCircle && (state$ | async) as state"
            >
                <circle
                    *ngIf="state !== 'submitted'; else submitted"
                    class="progress-ring__circle"
                    r="45"
                    cx="50"
                    cy="50"
                    [@circleAnimation]="{
                        value: pressed ? 'active' : 'inactive',
                        params: { duration: animationDuration },
                    }"
                />
                <ng-template #submitted>
                    <circle class="full-circle" r="45" cx="50" cy="50" />
                </ng-template>
            </svg>
        </button>
    `,
    styleUrls: ['./long-press-btn.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('circleAnimation', [
            state('active', style({ strokeDashoffset: 0 })),
            state('inactive', style({ strokeDashoffset: 282.6 })),
            transition(
                'inactive => active',
                animate('{{ duration }}ms linear'),
                {
                    params: { duration: 1000 },
                },
            ),
        ]),
    ],
})
export class LongPressBtnComponent {
    @Input() duration = 1000;
    @Input() threshold = 150;
    @Input() showCircle = true;
    @Input() disabled = false;
    @Output() longPress = new EventEmitter<MouseEvent>();
    @Output() tap = new EventEmitter<MouseEvent>();

    pressed = false;
    state$ = new BehaviorSubject<LongPressState>('active');

    /** Duración de la animación (duración total menos el threshold) */
    get animationDuration(): number {
        return Math.max(0, this.duration - this.threshold);
    }

    onPressStart(event: MouseEvent) {
        this.pressed = true;
    }

    onPressCancel(event: MouseEvent) {
        this.pressed = false;
    }

    onNormalClick(event: MouseEvent) {
        this.pressed = false;
        this.tap.emit(event);
    }

    onFinish(event: MouseEvent) {
        this.pressed = false;
        this.state$.next('submitted');
        this.longPress.emit(event);
        timer(200).subscribe(() => this.state$.next('active'));
    }
}
